/**
 * `.m-cb` — the square the catalogue's rows are selected with.
 *
 * A real `<input type="checkbox">` rather than the prototype's `<span>` around
 * a `tabIndex={-1}` input: CAE-1550 asks that selection be keyboard-reachable,
 * and the prototype's is not. The input is visually replaced, not hidden, so it
 * keeps its role, its checked state and its own focus ring.
 *
 * Three states, because two of its callers need two and one needs three.
 * `.m-cb.is-mixed` belongs to a "Select all": Edit › Fixtures deliberately has
 * none (selection is per-row only), so its boxes never reach the mixed state,
 * while the Add Outcomes modal's market head — which selects every outcome
 * under it — is exactly what reaches it (CAE-1553). That head used to carry its
 * own 120-line copy of this file; a strict superset of a kit component sitting
 * in a page folder is the drift this component was lifted here to stop.
 */
import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { CheckIcon } from './line-icons';

const SIZE = 18;

export interface FixtureSelectCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The accessible name, e.g. "Select Mexico vs South Africa". */
  label: string;
  /**
   * The row cannot be selected — the Add Fixtures modal's already-attached
   * fixtures, which it draws ticked and inert.
   *
   * A real `disabled`, not `aria-disabled`: unlike the detach button there is
   * nothing behind this to catch a click that falls through. The row head sits
   * under it and would expand, so the input swallowing its own clicks is the
   * behaviour wanted, and `disabled` already communicates the state.
   */
  disabled?: boolean;
  /**
   * Some but not all of what this box stands for is selected — a market head
   * whose outcomes are partly picked. Drawn as `.m-cb-bar`, a 9×1.5px rule on
   * the accent ground, and announced as `aria-checked="mixed"`.
   *
   * Set on the DOM node through a ref rather than as an attribute, because
   * there is no `indeterminate` content attribute — it exists only as an IDL
   * property, and it is the property a screen reader reads.
   */
  indeterminate?: boolean;
}

export const FixtureSelectCheckbox = ({
  checked,
  onChange,
  label,
  disabled = false,
  indeterminate = false,
}: FixtureSelectCheckboxProps) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const filled = checked || indeterminate;

  return (
    <Box
      component="label"
      // The row body listens for clicks to expand; selecting is not expanding.
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      sx={(theme: Theme) => ({
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
        width: `${SIZE}px`,
        height: `${SIZE}px`,
        borderRadius: `${theme.tokens.radius.badge - 1}px`,
        border: `1.5px solid ${
          filled ? theme.tokens.color.accent : theme.tokens.componentColor.metaSeparator
        }`,
        backgroundColor: filled ? theme.tokens.color.accent : theme.tokens.color.surface,
        color: theme.tokens.color.surface,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'background 0.12s, border-color 0.12s',
        ...(disabled
          ? {}
          : {
              '&:hover': {
                borderColor: filled ? theme.tokens.color.accent : theme.tokens.color.ink4,
              },
            }),
        '&:focus-within': {
          boxShadow: `0 0 0 3px ${theme.tokens.color.accentSoft}`,
          borderColor: theme.tokens.color.accent,
        },
      })}
    >
      <Box
        component="input"
        ref={ref}
        type="checkbox"
        aria-label={label}
        aria-checked={indeterminate ? 'mixed' : checked}
        checked={checked}
        disabled={disabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        sx={{
          position: 'absolute',
          inset: 0,
          margin: 0,
          opacity: 0,
          width: '100%',
          height: '100%',
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      {indeterminate ? (
        /* `.m-cb-bar` */
        <Box
          aria-hidden
          sx={(theme: Theme) => ({
            width: '9px',
            height: '1.5px',
            borderRadius: '1px',
            backgroundColor: theme.tokens.color.surface,
          })}
        />
      ) : null}
      {checked && !indeterminate ? <CheckIcon aria-hidden sx={{ fontSize: '12px' }} /> : null}
    </Box>
  );
};
